<template>
  <div
    v-if="!isEditing"
    class="flex flex-col flex-wrap md:flex-row items-stretch md:items-center justify-between p-6 bg-white border border-gray-100 shadow-sm rounded-2xl mb-6 gap-6"
  >
    <div
      class="flex flex-col justify-center pr-8 md:border-r border-gray-100 min-w-max"
    >
      <span
        class="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-1"
      >
        EXTERNAL ID
      </span>
      <h3 class="text-xl text-blue-600 font-black">#{{ detail._id }}</h3>
    </div>

    <div class="flex flex-col flex-grow justify-center">
      <div class="flex items-center gap-3">
        <h1
          class="text-2xl font-black text-gray-900 tracking-tight leading-tight uppercase"
        >
          {{ detail?.title || "Campaign Details" }}
        </h1>
      </div>
      <p class="text-[13px] font-medium text-gray-400 mt-1">
        {{ detail?.description || "" }}
      </p>
    </div>

    <div class="flex flex-col items-end justify-between gap-4 md:ms-auto w-full md:w-auto">
      <div
        class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black tracking-wider transition-all border"
        :class="
          detail.is_enabled
            ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
            : 'bg-rose-50 text-rose-600 border-rose-100'
        "
      >
        <span
          class="w-2 h-2 rounded-full"
          :class="
            detail.is_enabled ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
          "
        ></span>
        {{ detail.is_enabled ? "ACTIVE" : "INACTIVE" }}
      </div>

      <div v-if="showActionbtn" class="flex items-center gap-3 flex-wrap w-full !m-0 justify-start md:justify-end">
        <button
          @click="$emit('clone-rain')"
          class="inline-flex flex-1 md:flex-none  justify-center items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-5 h-5 text-gray-500"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
            />
          </svg>
          Clone
        </button>

        <router-link
          :to="{
            name: detail.mode === 'rain' ? 'RainsEdit' : 'ChallengesEdit',
            params: {
              id: detail._id,
              lang: $route.params.lang || 'en',
              ...($route.params.skinFilt || $store.getters.skinStorage || detail.skin_id
                ? {
                    skinFilt: (
                      $route.params.skinFilt ||
                      $store.getters.skinStorage ||
                      detail.skin_id
                    ).toString(),
                  }
                : {}),
            },
          }"
          class="inline-flex flex-1 md:flex-none justify-center items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-5 h-5 text-gray-500"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
            />
          </svg>
          Edit
        </router-link>

        <button
          v-if="detail.mode === 'rain'"
          @click="$emit('generate-rain')"
          :disabled="isGeneratingRain"
          class="inline-flex  flex-1 md:flex-none justify-center items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-all active:scale-95 shadow-md shadow-blue-100 disabled:opacity-70"
        >
          <i
            v-if="!isGeneratingRain"
            class="fa-solid fa-cloud-showers-heavy"
          ></i>
          <svg
            v-else
            class="animate-spin h-4 w-4 text-white"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
              fill="none"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {{ isGeneratingRain ? "Generating..." : "Generate Rain" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HeaderSection",
  props: {
    detail: {
      type: Object,
      required: true,
    },
    isEditing: Boolean,
    isGeneratingRain: Boolean,
    showActionbtn: {
      type: Boolean,
      default: true,
    },
  },
};
</script>
